import React, { useCallback, useMemo } from "react";

import { chakra, FormControl } from "@chakra-ui/react";
import { debounce } from "lodash";
import { components } from "react-select";

import { Select } from "@/components/forms/BaseForm/selects/Select";
import { Option } from "@/components/forms/types";
import { useCompanyMembers } from "@/hooks/features/useCompanyMembers";

interface UploadedByFilterProps {
  value: string | null;
  onChange: (value: string | null) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const UploadedByOption = (props: any) => {
  const { data, isSelected, children } = props;

  if (props.data?.value === "separator-company") {
    return <SeparatorOption {...props} />;
  }

  return (
    <components.Option {...props}>
      <chakra.div fontSize="13px">
        <chakra.div fontWeight="medium">{children}</chakra.div>
        {data?.secondaryLabel && (
          <chakra.div
            textColor={isSelected ? "gray.100" : "chakra-subtle-text"}
            fontSize="xs"
            mt={1}
          >
            {data.secondaryLabel}
          </chakra.div>
        )}
      </chakra.div>
    </components.Option>
  );
};

const SeparatorOption = (props: any) => {
  return (
    <components.Option {...props} isDisabled>
      <chakra.div
        fontSize="xs"
        fontWeight="medium"
        color="gray.500"
        textTransform="uppercase"
        textAlign="center"
        letterSpacing="wide"
        py={1}
      >
        {props.children}
      </chakra.div>
    </components.Option>
  );
};

export const UploadedByFilter: React.FC<UploadedByFilterProps> = ({
  value,
  onChange
}) => {
  const {
    clientMembers,
    companyMembers,
    isLoading,
    loadMembers,
    loadClientMembers,
    activeCompanyId,
    activeClientId
  } = useCompanyMembers();

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (activeClientId) {
        loadClientMembers(activeClientId, query);
      }
      if (activeCompanyId && activeCompanyId !== activeClientId) {
        loadMembers(activeCompanyId, query);
      }
    }, 300),
    [loadMembers, loadClientMembers, activeCompanyId, activeClientId]
  );

  const handleInputChange = useCallback(
    (newValue: string, actionMeta: { action: string }) => {
      if (actionMeta.action === "input-change") {
        debouncedSearch(newValue);
      }
    },
    [debouncedSearch]
  );

  const userOptions: Option<string>[] = useMemo(() => {
    const clientOptions = clientMembers.map(
      ({ id, first_name, last_name, email }) => ({
        value: id,
        label:
          first_name || last_name
            ? `${first_name} ${last_name}`
            : "Unnamed User",
        secondaryLabel: email
      })
    );
    clientOptions.sort((a, b) =>
      a.secondaryLabel.localeCompare(b.secondaryLabel)
    );
    clientOptions.sort((a, b) =>
      a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase())
    );

    const companyOptions = companyMembers.map(
      ({ id, first_name, last_name, email }) => ({
        value: id,
        label:
          first_name || last_name
            ? `${first_name} ${last_name}`
            : "Unnamed User",
        secondaryLabel: email
      })
    );
    companyOptions.sort((a, b) =>
      a.secondaryLabel.localeCompare(b.secondaryLabel)
    );
    companyOptions.sort((a, b) =>
      a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase())
    );

    const options: Option<string>[] = [];
    options.push(...clientOptions);
    if (clientMembers.length > 0 && companyMembers.length > 0) {
      options.push({
        value: "separator-company",
        label: "---",
        isDisabled: true
      });
    }
    options.push(...companyOptions);
    return options;
  }, [clientMembers, companyMembers]);

  // Convert empty string to null for the Select component
  const selectValue = value === "" ? null : value;

  return (
    <FormControl>
      <Select
        value={selectValue}
        onChange={onChange}
        options={userOptions}
        placeholder={isLoading ? "Loading users..." : "Select user..."}
        isSearchable={true}
        isClearable={true}
        searchMatchFrom="any"
        aria-label="Uploaded By"
        isLoading={isLoading}
        onInputChange={handleInputChange}
        components={{
          Option: UploadedByOption
        }}
      />
    </FormControl>
  );
};
